// src/ui/Welcome.tsx (T1): the builder's welcome video (docs/DECISIONS.md, "A welcome from ..."): a compact card
// at the top of Home, the quiet "Watch ...'s welcome" link it folds into (sidebar footer on desktop, footer on
// phone), and the player. Never autoplays, captions on by default, preload="none". Until the video is recorded,
// every environment shows the person's real photo, "A welcome from Mark" and "Video coming soon": no play button,
// no link and no player (docs/DECISIONS.md "Owner answers"). Never a stock or generated face.
// T5 owns where it shows and the per-viewer watched or dismissed state. Root classes: hb-welcome,
// hb-welcome-link, hb-welcome-player.
import { useRef, useState } from "preact/hooks";
import { duration } from "../lib/format";
import { Button, IconButton } from "./Button";
import { cx } from "./cx";
import { Icon } from "./Icon";
import { Sheet } from "./Sheet";
import { toast } from "./Toasts";

export type WelcomeVideoView = {
  /** "Mark" */
  firstName: string;
  /** Full name and role, from the tenant's people[]. */
  name: string;
  role: string;
  durationSec: number | null;
  src: string | null;
  srcSmall: string | null;
  poster: string | null;
  captions: string | null;
  /** The person's real photo (people[].photo), shown until the video is recorded. */
  photo?: string | null;
};

/** A recorded video plays; until then the card says "Video coming soon" and nothing plays. */
export function isRecorded(v: WelcomeVideoView): boolean {
  return Boolean(v.src);
}

function metaParts(v: WelcomeVideoView): string[] {
  const parts = [v.name, v.role];
  if (v.durationSec) parts.push(duration(v.durationSec));
  return parts.filter(Boolean);
}

export function WelcomeCard(props: { video: WelcomeVideoView; onPlay: () => void; onDismiss: () => void; class?: string }) {
  const v = props.video;
  if (!isRecorded(v)) return <WelcomeSoon video={v} onDismiss={props.onDismiss} class={props.class} />;
  const [head, ...rest] = metaParts(v);
  const len = v.durationSec ? `, ${Math.round(v.durationSec)} seconds` : "";
  return (
    <section class={cx("hb-welcome", "welcome", props.class)} aria-label={`A welcome from ${v.firstName}`}>
      <button class="wel-poster" type="button" onClick={props.onPlay} aria-label={`Play ${v.firstName}'s welcome${len}, captions on`}>
        {v.poster ? <img src={v.poster} alt="" /> : null}
        <span class="wel-play" aria-hidden="true">
          <Icon name="play" />
        </span>
        {v.poster ? null : (
          <span class="wel-ph" aria-hidden="true">
            {v.firstName}'s welcome video goes here
          </span>
        )}
      </button>
      <div class="wel-t">
        <h2>
          <button type="button" onClick={props.onPlay}>
            A welcome from {v.firstName}
          </button>
        </h2>
        <p class="wel-m">
          <span>{head}</span>
          {rest.length ? (
            <>
              <span class="wel-sep" aria-hidden="true">
                {" "}
                ·{" "}
              </span>
              <span>{rest.join(" · ")}</span>
            </>
          ) : null}
        </p>
      </div>
      {/* The phone's slim row reads "A welcome from Mark · 0:40" on one line; the name and role stay on desktop. */}
      {v.durationSec ? (
        <span class="wel-len num" aria-hidden="true">
          {duration(v.durationSec)}
        </span>
      ) : null}
      <IconButton class="wel-x" icon="x" label={`Close ${v.firstName}'s welcome. You can watch it later.`} onClick={props.onDismiss} />
    </section>
  );
}

/** Before the video exists: the person's photo, "A welcome from Mark" and "Video coming soon". Nothing plays. */
function WelcomeSoon(props: { video: WelcomeVideoView; onDismiss: () => void; class?: string }) {
  const v = props.video;
  return (
    <section class={cx("hb-welcome", "welcome", "is-soon", props.class)} aria-label={`A welcome from ${v.firstName}`}>
      <span class="wel-photo">{v.photo ? <img src={v.photo} alt={`${v.name}, ${v.role}`} width={96} height={96} decoding="async" /> : null}</span>
      <div class="wel-t">
        <h2>A welcome from {v.firstName}</h2>
        <p class="wel-m">Video coming soon</p>
      </div>
      <IconButton class="wel-x" icon="x" label={`Close ${v.firstName}'s welcome`} onClick={props.onDismiss} />
    </section>
  );
}

/** "Watch Mark's welcome (0:40)": `side` in the sidebar footer (desktop), `foot` in the footer (phone, tablet). */
export function WelcomeLink(props: { video: WelcomeVideoView; place: "side" | "foot"; onOpen: () => void }) {
  const v = props.video;
  return (
    <button type="button" class={cx("hb-welcome-link", props.place === "side" ? "side-welcome" : "foot-welcome")} onClick={props.onOpen}>
      <span class="wl-play" aria-hidden="true">
        <Icon name="play" />
      </span>
      <span class="wl-t">Watch {v.firstName}'s welcome</span>
      {v.durationSec ? <span class="num">({duration(v.durationSec)})</span> : null}
    </button>
  );
}

/** The player: a centred dialog on desktop, a bottom sheet on phone. `onPlayed` fires on the first play. */
export function WelcomePlayer(props: { video: WelcomeVideoView; open: boolean; onClose: () => void; onPlayed?: () => void }) {
  const v = props.video;
  const played = useRef(false);
  const [cc, setCc] = useState(true);
  const recorded = Boolean(v.src);
  const onPlay = () => {
    if (played.current) return;
    played.current = true;
    props.onPlayed?.();
  };
  return (
    <Sheet
      open={props.open}
      onClose={props.onClose}
      variant="modal"
      title={`A welcome from ${v.firstName}`}
      sub={metaParts(v).join(" · ")}
      class="hb-welcome-player wel-player"
    >
      {recorded ? (
        <div class="wp-stage">
          <video controls preload="none" playsInline poster={v.poster ?? undefined} onPlay={onPlay}>
            {v.srcSmall ? <source src={v.srcSmall} type="video/mp4" media="(max-width: 1023px)" /> : null}
            <source src={v.src!} type="video/mp4" />
            {v.captions ? <track kind="captions" src={v.captions} srcLang="en" label="English" default /> : null}
          </video>
        </div>
      ) : (
        <>
          <div class="wp-stage">
            <span class="wel-play" aria-hidden="true">
              <Icon name="play" />
            </span>
            <p class="wp-ph">{v.firstName}'s welcome video goes here</p>
            <p class="wp-demo">Demo placeholder</p>
          </div>
          <div class="wp-bar">
            <Button icon="play" onClick={() => toast(`${v.firstName}'s video plays here once it's recorded.`)}>
              Play
            </Button>
            <span class="wp-time num">0:00{v.durationSec ? ` / ${duration(v.durationSec)}` : ""}</span>
            <button class="chip wp-cc" type="button" aria-pressed={cc ? "true" : "false"} onClick={() => setCc(!cc)}>
              <Icon name="cc" size="sm" />
              <span>{cc ? "Captions On" : "Captions Off"}</span>
            </button>
          </div>
        </>
      )}
    </Sheet>
  );
}
